import { ChangeEvent, createContext, ReactNode, useCallback, useContext, useMemo } from 'react';
import { useRelayEnvironment } from 'react-relay';
import { fetchQuery } from 'relay-runtime';

import { passwordCheckQuery } from '@/__generated__/passwordCheckQuery.graphql';
import { StepType, useChangePasswordStep } from '@/components/my/password/hooks/use-change-password-step';
import { useChangePasswordValue } from '@/components/my/password/hooks/use-change-password-value';
import { useNewPassword } from '@/components/my/password/hooks/use-new-password';
import { MINIMUM } from '@/components/my/password/hooks/use-password-error';
import { checkPasswordQuery } from '@/graphql/query/my/password';

interface CheckPasswordParams {
  onCompleted: () => void;
  onError: () => void;
  onInvalid: () => void;
}

interface ChangePasswordContextModel {
  step: StepType;
  renderNextPage: () => void;
  renderPrevPage: () => void;
  password: string;
  newPassword: string;
  confirmPassword: string;

  checkPassword: (callback: CheckPasswordParams) => void;
  changePassword: (e: ChangeEvent<HTMLInputElement>) => void;
  changeNewPassword: (e: ChangeEvent<HTMLInputElement>) => void;
  changeConfirmPassword: (e: ChangeEvent<HTMLInputElement>) => void;

  isLengthError: boolean;
}

const ChangePasswordContext = createContext({} as ChangePasswordContextModel);

export const useChangePasswordContext = () => useContext(ChangePasswordContext);

export const ChangePasswordProvider = ({ children }: { children: ReactNode }) => {
  const { step, renderNextPage, renderPrevPage } = useChangePasswordStep();
  const { password, changePassword } = useChangePasswordValue();
  const { changeNewPassword, changeConfirmPassword, newPassword, confirmPassword } = useNewPassword();
  const relayEnv = useRelayEnvironment();

  const checkPassword = useCallback(
    ({ onCompleted, onError, onInvalid }: CheckPasswordParams) => {
      fetchQuery<passwordCheckQuery>(relayEnv, checkPasswordQuery, { password }).subscribe({
        next: (res) => {
          // 응답에 에러가 포함된 경우
          if (res.checkRefundUserPassword.errors) {
            onError();
            return;
          }

          // 에러가 없고 비밀번호 체크 성공
          if (res.checkRefundUserPassword.result) {
            onCompleted();
          } else {
            // 에러가 없으나 비밀번호 체크 실패
            onInvalid();
          }
        },
        error: () => {
          onError();
        }
      });
    },
    [password, relayEnv]
  );

  const initValue = useMemo(
    () => ({
      step,
      renderPrevPage,
      renderNextPage,
      password,
      newPassword,
      checkPassword,
      confirmPassword,
      changePassword,
      changeNewPassword,
      changeConfirmPassword,

      isLengthError: password.length < MINIMUM
    }),
    [step, renderPrevPage, renderNextPage, password, changePassword, checkPassword, newPassword, confirmPassword, changeConfirmPassword, changeNewPassword]
  );

  return <ChangePasswordContext.Provider value={initValue}>{children}</ChangePasswordContext.Provider>;
};
